import { z } from 'zod';
import type { ApiSchemaClass, ApiSchemaDefinition } from './types';
import { llmModelIdSchema } from './editor-state';
import { uiMessageArraySchema } from './ui-message';

// ============================================================
// Durable agent + workflow harness (raw ATC step handlers).
//
// A third-party surface that drives ATC's step handlers directly with the
// caller's system + messages + tool schemas VERBATIM — zero Coco injection.
// The caller owns tool execution (raw Anthropic-style loop), made durable by
// ATC/Restate. See apps/server/src/features/resource/harness.
// ============================================================

const harnessToolSchema = z
  .object({
    name: z
      .string()
      .describe(
        'Tool name the model may call. Returned verbatim in tool-calls.'
      ),
    description: z
      .string()
      .optional()
      .describe('Human/model-facing description of what the tool does.'),
    inputSchema: z
      .record(z.string(), z.unknown())
      .describe(
        'JSON Schema for the tool input. Rendered to the model verbatim; never executed server-side.'
      ),
  })
  .describe(
    'A caller-defined tool. The model emits tool-calls against it; the caller executes them and posts results back on the next step.'
  );

const harnessConfigSchema = z
  .object({
    maxOutputTokens: z.number().optional(),
    temperature: z.number().optional(),
    topP: z.number().optional(),
    frequencyPenalty: z.number().optional(),
    presencePenalty: z.number().optional(),
  })
  .describe('Optional LLM sampling configuration for this turn.');

const harnessUsageSchema = z
  .object({
    inputTokens: z.number(),
    outputTokens: z.number(),
    cachedInputTokens: z.number().optional(),
    cacheCreationInputTokens: z.number().optional(),
    reasoningTokens: z.number().optional(),
  })
  .describe('Token usage reported by the provider for this turn.');

const harnessToolCallSchema = z.object({
  toolCallId: z
    .string()
    .describe(
      'Provider tool-call id; echo it back in the tool-result message.'
    ),
  toolName: z.string().describe('Name of the tool the model called.'),
  input: z
    .unknown()
    .describe('Parsed tool input arguments the model produced.'),
});

// ── Create ──────────────────────────────────────────────────

export const harnessCreateBodyV20260901Schema = z.object({
  model: llmModelIdSchema
    .optional()
    .describe('Default model for the run. May be overridden per step.'),
  system: z
    .string()
    .optional()
    .describe(
      'Verbatim system prompt seeded on the run. No Coco system message is ever added.'
    ),
  messages: uiMessageArraySchema
    .optional()
    .describe('Optional initial messages seeded on the run.'),
  tools: z
    .array(harnessToolSchema)
    .optional()
    .describe(
      'Optional default tool schemas (informational; tools are passed per step).'
    ),
});

export type HarnessCreateBody = z.infer<
  typeof harnessCreateBodyV20260901Schema
>;

export const harnessCreateResponseSchema = z.object({
  runId: z
    .string()
    .uuid()
    .describe('Durable run id. Pass it to /step, /extract, GET, and /cancel.'),
});

// ── Step (one raw step-chat turn) ───────────────────────────

export const harnessStepBodyV20260901Schema = z.object({
  model: llmModelIdSchema.describe('Model to run this turn with.'),
  system: z
    .string()
    .optional()
    .describe('Verbatim system prompt for this turn. No Coco injection.'),
  messages: uiMessageArraySchema.describe(
    'Messages to append before running the turn. Post tool-result messages here between turns.'
  ),
  tools: z
    .array(harnessToolSchema)
    .optional()
    .describe(
      'Tool schemas the model may call this turn. The caller executes tool-calls.'
    ),
  cache: z
    .boolean()
    .optional()
    .describe('Opt into the LLM result cache. Defaults to false (bypass).'),
  config: harnessConfigSchema.optional(),
});

export type HarnessStepBody = z.infer<typeof harnessStepBodyV20260901Schema>;

export const harnessStepResponseSchema = z.object({
  stopReason: z
    .string()
    .describe(
      "Finish reason: 'stop' (final text), 'tool-calls' (execute and continue), 'length', 'error', etc."
    ),
  text: z.string().optional().describe('Assistant text output, when present.'),
  toolCalls: z
    .array(harnessToolCallSchema)
    .optional()
    .describe(
      'Tool-calls the model requested. Execute them and post results on the next step.'
    ),
  usage: harnessUsageSchema,
  position: z
    .number()
    .describe(
      'Conversation position of the assistant message produced by this turn.'
    ),
});

// ── Extract (one raw structured-extraction turn) ────────────

export const harnessExtractBodyV20260901Schema = z.object({
  model: llmModelIdSchema.describe('Model to run the extraction with.'),
  system: z
    .string()
    .optional()
    .describe('Verbatim system prompt for this turn. No Coco injection.'),
  messages: uiMessageArraySchema.describe(
    'Messages to append before extracting.'
  ),
  outputShape: z
    .string()
    .describe(
      'ERA type string describing the object to extract (e.g. "{ name: string, age: int }").'
    ),
  config: harnessConfigSchema.optional(),
});

export type HarnessExtractBody = z.infer<
  typeof harnessExtractBodyV20260901Schema
>;

export const harnessExtractResponseSchema = z.object({
  object: z
    .unknown()
    .describe('The validated object conforming to outputShape.'),
});

// ── Schema classes ──────────────────────────────────────────

export class HarnessCreateV20260901Schema implements ApiSchemaDefinition {
  static readonly version = 'harness-create-2026-09-01' as const;

  readonly name = HarnessCreateV20260901Schema.version;
  readonly schema = harnessCreateBodyV20260901Schema;
  readonly response = harnessCreateResponseSchema;
}

export class HarnessStepV20260901Schema implements ApiSchemaDefinition {
  static readonly version = 'harness-step-2026-09-01' as const;

  readonly name = HarnessStepV20260901Schema.version;
  readonly schema = harnessStepBodyV20260901Schema;
  readonly response = harnessStepResponseSchema;
}

export class HarnessExtractV20260901Schema implements ApiSchemaDefinition {
  static readonly version = 'harness-extract-2026-09-01' as const;

  readonly name = HarnessExtractV20260901Schema.version;
  readonly schema = harnessExtractBodyV20260901Schema;
  readonly response = harnessExtractResponseSchema;
}

export const HARNESS_API_SCHEMAS = [
  HarnessCreateV20260901Schema,
  HarnessStepV20260901Schema,
  HarnessExtractV20260901Schema,
] as const satisfies readonly ApiSchemaClass[];
